import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { formatDate as fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Printer } from "lucide-react";
import { BRAND } from "@/lib/brand";

function taxLabel(t) {
  if (!t || t === "none") return "No bill";
  if (t === "gst") return "GST bill";
  if (t === "local") return "Local purchase";
  return t.toUpperCase();
}

export default function PrintInvoice() {
  const { poId } = useParams();
  const [po, setPo] = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    api
      .get(`/purchase-orders/${poId}`)
      .then((r) => setPo(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setReady(true));
  }, [poId]);

  return (
    <div className="min-h-screen bg-white print-invoice">
      <style>{`
        @media print {
          @page { size: A4; margin: 16mm; }
          .no-print { display: none !important; }
          body { background: #fff !important; }
        }
        .print-invoice table { border-collapse: collapse; width: 100%; font-size: 12px; }
        .print-invoice th, .print-invoice td { border: 1px solid #ddd; padding: 6px 8px; vertical-align: top; }
        .print-invoice th { background: #F5F4F0; text-align: left; text-transform: uppercase; font-size: 10px; letter-spacing: .04em; color: #4A4543; }
        .print-invoice .num { text-align: right; font-variant-numeric: tabular-nums; }
      `}</style>

      <div className="max-w-[900px] mx-auto p-10">
        <div className="flex items-start justify-between mb-6 pb-6 border-b">
          <div>
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Purchase Order / Invoice</div>
            <h1 className="font-heading text-3xl">{BRAND.name}</h1>
            <div className="text-xs text-muted-foreground mt-1">Cosmetic shop · back-office bill</div>
          </div>
          <div className="no-print flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                if (window.opener) window.close();
                else window.history.length > 1 ? window.history.back() : (window.location.href = "/orders");
              }}
              data-testid="invoice-close-btn"
            >
              Close
            </Button>
            <Button
              onClick={() => window.print()}
              style={{ backgroundColor: "#9E6D5B" }}
              data-testid="invoice-print-trigger"
            >
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
          </div>
        </div>

        {!ready ? (
          <div className="text-sm text-muted-foreground">Loading…</div>
        ) : !po ? (
          <div className="text-sm text-muted-foreground">Purchase order not found.</div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-6 mb-6 text-sm">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Supplier</div>
                <div className="font-medium">{po.supplier_name}</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">PO ID</div>
                <div className="font-mono text-xs">{po.id}</div>
                {po.invoice_number && (
                  <>
                    <div className="text-xs uppercase tracking-widest text-muted-foreground mt-2 mb-1">Invoice #</div>
                    <div className="font-mono">{po.invoice_number}</div>
                  </>
                )}
              </div>
            </div>

            <div className="grid grid-cols-4 gap-4 mb-6 text-sm">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Date</div>
                <div>{fmtDate(po.created_at)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Status</div>
                <div className="uppercase font-medium">{po.payment_status}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Mode</div>
                <div className="uppercase">{po.payment_mode}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Due</div>
                <div>{fmtDate(po.due_date)}</div>
              </div>
            </div>

            <table>
              <thead>
                <tr>
                  <th style={{ width: 32 }}>#</th>
                  <th>Product</th>
                  <th>Brand</th>
                  <th>Barcode</th>
                  <th className="num">Qty</th>
                  <th className="num">Rate</th>
                  <th className="num">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(po.line_items || []).map((l, i) => {
                  const p = l.product_snapshot || {};
                  return (
                    <tr key={l.id || `${p.product}-${l.quantity}-${l.rate}`}>
                      <td>{i + 1}</td>
                      <td>{p.product}</td>
                      <td>{p.brand_name}</td>
                      <td>{p.auto_barcode}</td>
                      <td className="num">{l.quantity}</td>
                      <td className="num">₹{Number(l.rate || 0).toFixed(2)}</td>
                      <td className="num">₹{Number(l.amount || 0).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={6} className="num" style={{ fontWeight: 600 }}>
                    Subtotal
                  </td>
                  <td className="num">₹{Number(po.subtotal || 0).toFixed(2)}</td>
                </tr>
                {po.tax_type !== "none" && (
                  <tr>
                    <td colSpan={7} className="num" style={{ fontSize: 11, color: "#4A4543" }}>
                      Bill type: {taxLabel(po.tax_type)}
                    </td>
                  </tr>
                )}
                <tr>
                  <td colSpan={6} className="num" style={{ fontWeight: 700, background: "#F5F4F0" }}>
                    Total
                  </td>
                  <td className="num" style={{ fontWeight: 700, background: "#F5F4F0" }}>
                    ₹{Number(po.total || 0).toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>

            {po.notes && (
              <div className="mt-6 text-sm">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Notes</div>
                <div className="whitespace-pre-wrap">{po.notes}</div>
              </div>
            )}

            <div className="mt-12 grid grid-cols-2 gap-10 text-xs">
              <div>
                <div className="uppercase tracking-widest text-muted-foreground mb-6">Received by</div>
                <div className="border-t pt-2">Signature / Name</div>
              </div>
              <div>
                <div className="uppercase tracking-widest text-muted-foreground mb-6">Authorised by</div>
                <div className="border-t pt-2">Signature / Name</div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
